import { useEffect, useMemo, useState } from "react";
import { Input, TextArea } from "sr/helpers";
import { MainWrapper } from "../component/MainWrapper";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { getAddress } from "sr/utils/api/individual/address/getAddress";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { useFormik } from "formik";
import { addJob } from "sr/utils/api/individual/job/addJob";
import { LoaderFullScreen } from "sr/layout/common/LoaderFullScreen";
import getLabelByValue from "sr/helpers/functions/getLabelByValue";
import { ReactDatePicker } from "sr/helpers/ui-components/ReactDatePicker";
import { IndJobSchema } from "sr/utils/schemas/individual/IndJobSchema";
import AutoLocation from "sr/helpers/ui-components/AutoLocation";
import { getJobTypeCategory } from "sr/utils/api/getJobTypeCategory";
import { ReactTimePicker } from "sr/helpers/ui-components/ReactTimePicker";
import { getJob } from "sr/utils/api/individual/job/getJob";
import { updateJob } from "sr/utils/api/individual/job/updateJob";
import { toast } from "react-toastify";
export const JobPostDetails = () => {
  const intl = useIntl();
  const [showAddress, setShowAddress] = useState(true);
  const [description, setDescription] = useState("");
  const [userId, setUserId] = useState<string>("");
  const [locations, setLocations] = useState([]);
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [from, setFrom] = useState("00:00");
  const [to, setTo] = useState("23:59");
  const [title, setTitle] = useState<any>();
  const [customError, setCustomError] = useState(null);
  const [address, setAddress] = useState(null);
  const [sendAddress, setSendAddress] = useState(null);
  const [unitorapt, setUnitOrApt] = useState("");
  const [id, setId] = useState<string>("");
  const formatTime = (date: Date) =>
    `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  const toMinutes = (value?: string) => {
    if (!value) return 0;
    const [h = "0", m = "0"] = value.split(":");
    return Number(h) * 60 + Number(m);
  };
  const isSelectedDateToday = useMemo(() => {
    if (!startDate) return false;
    const today = new Date();
    return new Date(startDate).toDateString() === today.toDateString();
  }, [startDate]);
  const disablePastHoursToday = useMemo(() => {
    if (!isSelectedDateToday) return undefined;
    const currentHour = new Date().getHours();
    return () => Array.from({ length: currentHour }, (_, i) => i);
  }, [isSelectedDateToday]);
  const disablePastMinutesToday = useMemo(() => {
    if (!isSelectedDateToday) return undefined;
    const currentHour = new Date().getHours();
    const currentMinute = new Date().getMinutes();
    return (selectedHour: number) => {
      if (selectedHour !== currentHour) return [];
      return Array.from({ length: currentMinute + 1 }, (_, i) => i);
    };
  }, [isSelectedDateToday]);
  useEffect(() => {
    if (!isSelectedDateToday) return;
    const now = new Date();
    const nowStr = formatTime(now);
    if (from && toMinutes(from) < toMinutes(nowStr)) {
      setFrom(nowStr);
    }
    if (to && toMinutes(to) < toMinutes(nowStr)) {
      setTo(nowStr);
    }
    if (from && to && toMinutes(to) < toMinutes(from)) {
      setTo(from);
    }
  }, [isSelectedDateToday]);
  useEffect(() => {
    const nowStr = formatTime(new Date());
    if (isSelectedDateToday) {
      setFrom(nowStr);
      setTo("23:59");
    } else {
      setFrom("00:00");
      setTo("23:59");
    }
  }, [isSelectedDateToday]);
  const minScheduleDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  useEffect(() => {
    const { user_id } = getUserDetails();
    setUserId(user_id);
  }, []);
  const params = useParams();
  useEffect(() => {
    const { title, id } = params;
    if (title) {
      setTitle(title);
    }
    if (id) {
      setId(id);
    }
  }, [params]);
  const HandleAddress = () => {
    if (address?.address_line_1) {
      setShowAddress(false);
    } else {
      setCustomError((value) => ({
        ...value,
        streetAddress: "INPUT.VALIDATION.INDIVIDUAL.STREET.ADDRESS",
      }));
    }
  };
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialValues = {
    streetAddress: address?.address_line_1,
    unitorapt: unitorapt,
    lat: address?.lat,
    lng: address?.lng,
    description: description,
    schedule_date: startDate,
    title: title,
    from: from,
    to: to,
    individual_id: userId,
    work_status: "open",
    timezone: tz,
  };
  const navigate = useNavigate();
  const { handleSubmit, errors } = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: IndJobSchema,
    onSubmit: async (value) => {
      setLoader(true);
      try {
        if (id) {
          const res = await updateJob({ id: id, ...value });
          if (res?.success === true) {
            navigate(
              `/individual/cleaner-profiles/${from}/${to}/${res?.data?.id}`
            );
          }
        } else {
          const jobDraft = { ...value };
          navigate(`/individual/cleaner-profiles/${from}/${to}/draft`, {
            state: { jobDraft },
          });
        }
      } finally {
        setLoader(false);
      }
    },
  });
  const HandleSubmit = () => {
    if (isSelectedDateToday) {
      const nowStr = formatTime(new Date());
      const nowMinutes = toMinutes(nowStr);
      if (from && toMinutes(from) < nowMinutes) {
        toast.error(
          intl.formatMessage({
            id: "INDIVIDUAL.TIME.ERROR.START_PAST",
            defaultMessage:
              "Selected start time has already passed. Please choose a future time.",
          })
        );
        setFrom(nowStr);
        return;
      }
      if (to && toMinutes(to) < nowMinutes) {
        toast.error(
          intl.formatMessage({
            id: "INDIVIDUAL.TIME.ERROR.END_PAST",
            defaultMessage:
              "Selected end time has already passed. Please choose a future time.",
          })
        );
        setTo(nowStr);
        return;
      }
      if (from && to && toMinutes(to) <= toMinutes(from)) {
        toast.error(
          intl.formatMessage({
            id: "INDIVIDUAL.TIME.ERROR.END_AFTER_START",
            defaultMessage: "End time must be after start time.",
          })
        );
        setTo(from);
        return;
      }
    }
    if (from) {
      if (to) {
        handleSubmit();
      } else {
        setCustomError((value) => ({
          ...value,
          to: "INPUT.VALIDATION.INDIVIDUAL.TO",
        }));
      }
    } else {
      setCustomError({ from: "INPUT.VALIDATION.INDIVIDUAL.FROM" });
    }
  };
  const fetchLocation = async (userId) => {
    try {
      const params = new URLSearchParams();
      if (userId) params.append("individual_id", userId);
      params.append("status!", "deleted");
      const res = await getAddress(params);
      if (res?.success === true) {
        setLocations(res?.data);
        // setLoader('')
      } else {
        // setLoader('')
      }
    } catch (e: any) {
      // setLoader('')
    }
  };
  useEffect(() => {
    if (userId) {
      fetchLocation(userId);
    }
  }, [userId]);
  const HandleAutoLocation = (data) => {
    if (data?.lat && data?.lng) {
      setAddress(data);
    }
  };
  const [jobTypes, setJobTypes] = useState([]);
  useEffect(() => {
    const fetchJobType = async () => {
      setLoader(true);
      try {
        const res = await getJobTypeCategory();
        if (res?.success === true) {
          setJobTypes(res?.data);
          setLoader(false);
        }
      } catch (e: any) {
        setLoader(false);
      }
    };
    fetchJobType();
  }, []);
  useEffect(() => {
    if (customError?.unitorapt) {
      delete customError["unitorapt"];
    }
  }, [unitorapt]);
  const fetchJobData = async (id) => {
    const queryParams = new URLSearchParams();
    queryParams.append("id", id);
    try {
      const res = await getJob(queryParams);
      if (res?.success === true) {
        setTitle(res?.data?.title);
        setStartDate(res?.data?.schedule_date);
        setDescription(res?.data?.description);
        setAddress({
          address_line_1: res?.data?.streetAddress,
          lat: res?.data?.lat,
          lng: res?.data?.lng,
        });
        setSendAddress({ address_line_1: res?.data?.streetAddress });
        setUnitOrApt(res?.data?.unitorapt);
      } else {
      }
    } catch (e) {}
  };
  useEffect(() => {
    if (id) {
      fetchJobData(id);
    }
  }, [id]);
  const topHeader = (
    <div className="bg-white flex justify-center w-full space-x-2 border border-neutral-200 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className="w-6 h-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
        />
      </svg>
      <p>
        <FormattedMessage
          id="INDIVIDUAL.BOOKTASK.TOP.DESCRIPTION"
          defaultMessage="Tell us about your task. We use these details to show cleaner in your area who fit your needs."
        />
      </p>
    </div>
  );
  return (
    <MainWrapper topHeader={topHeader}>
      {loader && <LoaderFullScreen />}
      <div className="">
        <div className="lg:mx-10 mx-0 md:mx-5 py-5">
          {title && jobTypes?.length > 0 && (
            <p className="text-lg  pb-2">
              <FormattedMessage
                id={getLabelByValue(title, jobTypes)}
                defaultMessage={"Post Your Job"}
              ></FormattedMessage>{" "}
            </p>
          )}
          <div className="bg-white rounded-md p-4 text-base border border-neutral-300 mb-3  placeholder:text-black">
            <div className="flex justify-between">
              <p className="text-base pb-3 ">
                <FormattedMessage
                  id="INDIVIDUAL.BOOKTASK.INPUT.TASK.LOCATION.TITLE"
                  defaultMessage="Your job location"
                />
              </p>
              {!showAddress && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-[#0078ae]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              )}
            </div>
            {showAddress ? (
              <>
                <div>
                  <AutoLocation
                    errors={{ address_line_1: customError?.streetAddress }}
                    componenttype="individualJob"
                    address={sendAddress}
                    noshowMap={true}
                    HandleAutoLocation={HandleAutoLocation}
                    noOther={true}
                    label={intl.formatMessage({
                      id: "INDIVIDUAL.BOOKTASK.INPUT.TASK.STREET.PLACEHOLDER",
                      defaultMessage: "Street address",
                    })}
                    placeholder={intl.formatMessage({
                      id: "INDIVIDUAL.BOOKTASK.INPUT.TASK.STREET.PLACEHOLDER",
                      defaultMessage: "Street address",
                    })}
                  ></AutoLocation>
                  <Input
                    error={errors?.unitorapt || customError?.unitorapt}
                    onChange={(e: any) => setUnitOrApt(e.target.value)}
                    value={unitorapt}
                    inputClass="border border-neutral-400 rounded-md h-11  placeholder:text-black"
                    InputDivClass="mt-2 mb-3"
                    placeholder={intl.formatMessage({
                      id: "INDIVIDUAL.BOOKTASK.INPUT.TASK.UNIT.PLACEHOLDER",
                      defaultMessage: "Unit or Apt #",
                    })}
                  ></Input>
                  {locations?.map((item, inbox) => (
                    <div className="flex space-x-2 items-center justify-start w-full cursor-pointer border-b-2 border-neutral-300">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-6 w-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                        />
                      </svg>

                      <p
                        onClick={() => {
                          const addr = item?.address;

                          // Build full formatted address like Google suggestion
                          const fullAddress = [
                            addr?.address_line_1,
                            addr?.address_line_2,
                            addr?.city,
                            addr?.state,
                            addr?.postal,
                            addr?.country === "US"
                              ? "United States"
                              : addr?.country,
                          ]
                            .filter(Boolean)
                            .join(", ");

                          setAddress({
                            address_line_1: fullAddress, // ← full string used internally
                            lat: addr?.lat,
                            lng: addr?.lng,
                            postal: addr?.postal,
                          });
                          setSendAddress({
                            address_line_1: fullAddress, // ← this drives AutoLocation display
                          });
                        }}
                        className="py-1"
                      >
                        {[
                          item?.address?.address_line_1,
                          item?.address?.city,
                          item?.address?.state,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-center pt-3">
                  <div
                    onClick={HandleAddress}
                    className="text-center rounded-md bg-[#ff39b3] font-bold px-5 py-2  text-white cursor-pointer hover:bg-navy  placeholder:text-black"
                  >
                    <FormattedMessage
                      id="INDIVIDUAL.BOOKTASK.CONTINUE.BUTTON"
                      defaultMessage="Continue"
                    />
                  </div>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <div className="flex space-x-2 items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                    />
                  </svg>
                  <p>{address?.address_line_1}</p>
                </div>
                <p className="text-[#0078ae]">
                  Good news, JaniJobs is available in your area
                </p>
              </div>
            )}
          </div>
          <div className="bg-white rounded-md p-4 text-base border border-neutral-300 mb-3">
            <div className="flex justify-between">
              <p className="text-base pb-3 ">
                <FormattedMessage
                  id="INDIVIDUAL.BOOKTASK.INPUT.TASK.TITLE"
                  defaultMessage="Tell us the details of your task"
                />
              </p>
              {!showAddress && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-[#0078ae]"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 12.75l6 6 9-13.5"
                  />
                </svg>
              )}
            </div>
            <TextArea
              divClassName="mt-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder={intl.formatMessage({
                id: "INDIVIDUAL.BOOKTASK.INPUT.TASK.PLACEHOLDER",
                defaultMessage:
                  "Provide a summary of what you need done for your tasker.",
              })}
              className="flex-1 p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-md border border-gray-300 focus:border-[#0078ae] focus:shadow-inputshadow outline-0  placeholder:text-black"
              error={errors?.description}
            />
          </div>
          <div className="bg-white rounded-md p-4 text-base border border-neutral-300 mb-3">
            <ReactDatePicker
              error={errors?.schedule_date}
              onChange={(date) => setStartDate(date)}
              selected={startDate}
              minDate={minScheduleDate}
              label={
                <FormattedMessage
                  id="RECRUITER.SCHEDULEWORKORDER.INPUT.SCHEDULEDATE"
                  defaultMessage="Schedule Date"
                />
              }
              inputClass="rounded-md  placeholder:text-black"
              labelClass="mt-2 text-black"
              InputDivClass="mt-0 w-26 pr-2 pb-2"
              type="date"
              placeholder="Schedule date"
            ></ReactDatePicker>
            <div className="mt-5">
              <p className="text-black text-base ">Time Range</p>
              <ReactTimePicker
                labelClass="mt-2 text-gray-700"
                InputDivClass="mt-2 pr-2 pb-2"
                error={customError?.from}
                onChange={(value) => setFrom(value)}
                disabledHours={disablePastHoursToday}
                disabledMinutes={disablePastMinutesToday}
                value={from}
                showSecond={false}
                label={
                  <FormattedMessage
                    id="INDIVIDUAL.CLEANER.SCHEDULEWORKORDER.INPUT.FROM"
                    defaultMessage="From"
                  />
                }
              ></ReactTimePicker>

              <ReactTimePicker
                labelClass="mt-2 text-gray-700"
                InputDivClass="mt-0 pr-2 pb-2"
                error={customError?.to}
                value={to}
                disabledHours={disablePastHoursToday}
                disabledMinutes={disablePastMinutesToday}
                onChange={(value) => setTo(value)}
                showSecond={false}
                label={
                  <FormattedMessage
                    id="INDIVIDUAL.CLEANER.SCHEDULEWORKORDER.INPUT.TO"
                    defaultMessage="To"
                  />
                }
              ></ReactTimePicker>
            </div>
            <div className="flex justify-center pt-3 space-x-2">
              <div
                onClick={HandleSubmit}
                className="text-center rounded-md bg-[#ff39b3] font-bold px-5 py-2  text-white cursor-pointer hover:bg-navy"
              >
                <FormattedMessage
                  id="INDIVIDUAL.BOOKTASK.CLEANERSSHOW.BUTTON"
                  defaultMessage="See Cleaner & Prices"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainWrapper>
  );
};
